import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { type Task, type UpdateTaskPayload } from "@/services/task.service";
import { type Project } from "@/services/project.service";
import { type Profile } from "@/services/profile.service";
import { type Sprint } from "@/services/sprint.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import { SYSTEM_STAGES } from "./system-stages";
import { formatTime, type UiTask } from "./types";
import { mergeText, mergeDescription } from "./conflict-merge";
import { ProjectDescriptionEditor } from "@/components/projects/project-description";
import { projectDescriptionText } from "@/components/projects/project-description-utils";

export interface MergedText {
	title?: string;
	description?: string;
	developer_notes?: string;
}

interface Props {
	open: boolean;
	base: UiTask; // values B loaded
	payload: UpdateTaskPayload; // B's pending edits (delta)
	theirs: Task; // server's current values (the other user's save)
	projects: Project[];
	profiles: Profile[];
	sprints: Sprint[];
	stages?: WorkflowStage[];
	submitting: boolean;
	onMerge: (text: MergedText) => void;
	onOverride: () => void;
	onCancel: () => void;
}

type FieldKind = "text" | "struct";
type Val = string | number | string[] | null;

function cap(s: string): string {
	return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function fmtDate(v: Val): string {
	if (!v || typeof v !== "string") return "None";
	const d = new Date(v);
	return isNaN(d.getTime()) ? "None" : d.toLocaleDateString();
}

function eq(a: Val, b: Val): boolean {
	if (Array.isArray(a) || Array.isArray(b)) {
		const aa = Array.isArray(a) ? a : [];
		const bb = Array.isArray(b) ? b : [];
		return aa.length === bb.length && aa.every((x, i) => x === bb[i]);
	}
	return a === b;
}

export function TaskConflictDialog({
	open,
	base,
	payload,
	theirs,
	projects,
	profiles,
	sprints,
	stages,
	submitting,
	onMerge,
	onOverride,
	onCancel,
}: Props) {
	const stageName = (key: Val) =>
		(stages ?? SYSTEM_STAGES).find((s) => s.key === key)?.name ??
		String(key ?? "—");
	const profileName = (id: Val) => {
		if (!id || typeof id !== "string") return "Unassigned";
		const p = profiles.find((x) => x.id === id);
		return p?.full_name || p?.email || "Unknown";
	};
	const projectName = (id: Val) =>
		projects.find((p) => p.id === id)?.name ?? "—";
	const sprintName = (id: Val) =>
		id && typeof id === "string"
			? (sprints.find((s) => s.id === id)?.name ?? "Unknown sprint")
			: "No sprint";
	const tagsLabel = (t: Val) =>
		Array.isArray(t) && t.length ? t.join(", ") : "None";
	const textDisp = (v: Val) => (v ? String(v) : "—");

	const has = (k: keyof UpdateTaskPayload) => k in payload;
	const pv = (k: keyof UpdateTaskPayload): Val =>
		payload[k] as unknown as Val;

	// Field model: base value (B loaded), their value (server now), my value
	// (B's edit if touched, else base), plus a display formatter.
	interface FieldRow {
		key: keyof UpdateTaskPayload;
		label: string;
		kind: FieldKind;
		base: Val;
		their: Val;
		my: Val;
		disp: (v: Val) => string;
	}

	const fields: FieldRow[] = [
		{
			key: "title",
			label: "Title",
			kind: "text",
			base: base.title,
			their: theirs.title ?? "",
			my: has("title") ? (pv("title") ?? "") : base.title,
			disp: textDisp,
		},
		{
			key: "description",
			label: "Description",
			kind: "text",
			base: base.description ?? "",
			their: theirs.description ?? "",
			my: has("description")
				? (pv("description") ?? "")
				: (base.description ?? ""),
			disp: (v) => projectDescriptionText(String(v ?? "")) || "—",
		},
		{
			key: "developer_notes",
			label: "Notes",
			kind: "text",
			base: base.developer_notes ?? "",
			their: theirs.developer_notes ?? "",
			my: has("developer_notes")
				? (pv("developer_notes") ?? "")
				: (base.developer_notes ?? ""),
			disp: textDisp,
		},
		{
			key: "status",
			label: "Status",
			kind: "struct",
			base: base.apiStatus,
			their: theirs.status,
			my: has("status") ? pv("status") : base.apiStatus,
			disp: stageName,
		},
		{
			key: "priority",
			label: "Priority",
			kind: "struct",
			base: base.priority,
			their: theirs.priority,
			my: has("priority") ? pv("priority") : base.priority,
			disp: (v) => cap(String(v ?? "")),
		},
		{
			key: "assigned_to",
			label: "Assignee",
			kind: "struct",
			base: base.assigned_to?.id ?? null,
			their: theirs.assigned_to?.id ?? null,
			my: has("assigned_to")
				? (pv("assigned_to") ?? null)
				: (base.assigned_to?.id ?? null),
			disp: profileName,
		},
		{
			key: "sprint_id",
			label: "Sprint",
			kind: "struct",
			base: base.sprint?.id ?? null,
			their: theirs.sprint?.id ?? null,
			my: has("sprint_id")
				? (pv("sprint_id") ?? null)
				: (base.sprint?.id ?? null),
			disp: sprintName,
		},
		{
			key: "project_id",
			label: "Project",
			kind: "struct",
			base: base.project_id,
			their: theirs.project_id,
			my: has("project_id") ? pv("project_id") : base.project_id,
			disp: projectName,
		},
		{
			key: "due_date",
			label: "Due date",
			kind: "struct",
			base: base.due_date,
			their: theirs.due_date,
			my: has("due_date") ? (pv("due_date") ?? null) : base.due_date,
			disp: fmtDate,
		},
		{
			key: "estimated_time",
			label: "Estimate",
			kind: "struct",
			base: base.estimated_time,
			their: theirs.estimated_time ?? 0,
			my: has("estimated_time")
				? (pv("estimated_time") ?? 0)
				: base.estimated_time,
			disp: (v) => formatTime(Number(v ?? 0)),
		},
		{
			key: "tags",
			label: "Tags",
			kind: "struct",
			base: base.tags,
			their: theirs.tags ?? [],
			my: has("tags") ? (pv("tags") ?? []) : base.tags,
			disp: tagsLabel,
		},
	];

	const rows = fields
		.map((f) => {
			const bEdited = has(f.key);
			const aChanged = !eq(f.base, f.their);
			const collision =
				bEdited && aChanged && !eq(f.my, f.their) && f.kind === "text";
			return { ...f, bEdited, aChanged, collision };
		})
		.filter((r) => r.bEdited || r.aChanged);

	const textCollisions = rows.filter((r) => r.collision);

	// Editable merged text, seeded from a 3-way merge per colliding text field.
	const [merged, setMerged] = useState<Record<string, string>>(() => {
		const init: Record<string, string> = {};
		for (const r of textCollisions) {
			const merge = r.key === "description" ? mergeDescription : mergeText;
			init[r.key] = merge(
				String(r.base ?? ""),
				String(r.my ?? ""),
				String(r.their ?? ""),
			);
		}
		return init;
	});

	const buildResult = (): MergedText => {
		const out: MergedText = {};
		for (const r of textCollisions) {
			(out as Record<string, string>)[r.key] = merged[r.key] ?? "";
		}
		return out;
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) onCancel();
			}}
		>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-warning" />
						Task changed while you were editing
					</DialogTitle>
					<DialogDescription>
						Someone else updated this task before you saved. Review the
						differences and choose how to resolve them.
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
					{rows.length > 0 ? (
						<div className="overflow-hidden rounded-md border border-border">
							<div className="grid grid-cols-[110px_1fr_1fr_92px] bg-muted-subtle/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
								<span>Field</span>
								<span>Theirs</span>
								<span>Yours</span>
								<span>On merge</span>
							</div>
							{rows.map((r) => (
								<div
									key={r.key}
									className="grid grid-cols-[110px_1fr_1fr_92px] border-t border-border px-3 py-2 text-sm"
								>
									<span className="text-muted">{r.label}</span>
									<span
										className="truncate text-muted"
										title={r.disp(r.their)}
									>
										{r.disp(r.their)}
									</span>
									<span
										className="truncate text-foreground"
										title={r.disp(r.my)}
									>
										{r.bEdited ? r.disp(r.my) : "—"}
									</span>
									<span className="text-[11px] font-medium">
										{r.bEdited ? (
											<span className="text-primary">Yours</span>
										) : (
											<span className="text-muted">Kept</span>
										)}
									</span>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-muted">
							No conflicting field changes detected.
						</p>
					)}

					{textCollisions.length > 0 && (
						<>
							<Separator />
							<section className="space-y-4">
								<p className="text-[11px] font-semibold uppercase tracking-wider text-muted">
									Merge text (both edited — review the combined
									result)
								</p>
								{textCollisions.map((r) => (
									<div key={r.key} className="space-y-1.5">
										<label className="text-sm font-medium text-foreground">
											{r.label}
										</label>
										{r.key === "description" ? (
											<div className="rounded-md border border-border px-3 py-2">
												<ProjectDescriptionEditor
													value={merged[r.key] ?? ""}
													onChange={(v) =>
														setMerged((m) => ({
															...m,
															[r.key]: v,
														}))
													}
													placeholder="Describe the task..."
												/>
											</div>
										) : (
											<textarea
												className="min-h-[96px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
												value={merged[r.key] ?? ""}
												onChange={(e) =>
													setMerged((m) => ({
														...m,
														[r.key]: e.target.value,
													}))
												}
											/>
										)}
									</div>
								))}
							</section>
						</>
					)}
				</div>

				<DialogFooter className="gap-2 sm:justify-between">
					<Button
						variant="ghost"
						onClick={onCancel}
						disabled={submitting}
					>
						Cancel
					</Button>
					<div className="flex gap-2">
						<Button
							variant="outline"
							onClick={onOverride}
							disabled={submitting}
						>
							Override theirs
						</Button>
						<Button
							onClick={() => onMerge(buildResult())}
							disabled={submitting}
						>
							Merge
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
